import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import PDFHeader from './PDFHeader';

const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 11, fontFamily: 'Helvetica', color: '#212529', backgroundColor: '#FFFFFF' },
  container: { width: 520, alignSelf: 'center' },
  detailsBlock: { alignSelf: 'flex-end', width: '60%', marginBottom: 16, backgroundColor: '#F9FAFB', padding: 10, borderRadius: 6, border: '1px solid #E5E7EB' },
  detailsRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', gap: 12, marginBottom: 16 },
  card: { flex: 1, border: '1px solid #E5E7EB', borderRadius: 6, padding: 12, backgroundColor: '#FFFFFF' },
  cardTitle: { color: '#1E40AF', fontWeight: 700, fontSize: 12, marginBottom: 8, borderBottom: '1px solid #E5E7EB', paddingBottom: 4 },
  label: { color: '#6B7280', fontSize: 9, marginBottom: 2 },
  value: { fontWeight: 600, fontSize: 10, color: '#1F2937' },
  sectionTitle: { color: '#1E40AF', fontWeight: 700, marginTop: 16, marginBottom: 8, fontSize: 14, borderLeft: '3px solid #2563EB', paddingLeft: 8 },
  divider: { borderTop: '1px solid #E5E7EB', marginVertical: 12 },
  table: { borderTop: '1px solid #E5E7EB', marginTop: 8 },
  tableRow: { flexDirection: 'row', paddingVertical: 6, borderBottom: '1px solid #F3F4F6' },
  tableHeader: { backgroundColor: '#F0F9FF', fontWeight: 700 },
  treatmentCol: { width: '30%' },
  categoryCol: { width: '15%' },
  teethCol: { width: '15%' },
  quantityCol: { width: '10%', textAlign: 'center' },
  durationCol: { width: '10%', textAlign: 'right' },
  costCol: { width: '20%', textAlign: 'right' },
  footer: { marginTop: 24, paddingTop: 12, borderTop: '1px solid #E5E7EB', textAlign: 'center' },
  muted: { color: '#6B7280', fontSize: 9 },
  totalRow: { backgroundColor: '#EFF6FF', paddingVertical: 10, marginTop: 8, borderTop: '2px solid #2563EB', borderBottom: '2px solid #2563EB', borderRadius: 4 },
  totalText: { fontWeight: 700, fontSize: 12, color: '#1E40AF' }
});

const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A');
// Use number + currency code (symbols like ₹/₪ may render as ¹ in PDF fonts)
const formatPrice = (price, currency = 'INR') => {
  if (!price && price !== 0) return 'N/A';
  const formatted = Number(price).toLocaleString('en', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return `${formatted} ${currency}`;
};

export default function TreatmentPlanPDF({ plan, organization, clinic, currency: currencyProp }) {
  const patient = plan?.patient || {};
  const treatments = plan?.treatments || [];
  const clinicFromPatient = patient?.registeredClinic || clinic || {};
  const currency = currencyProp || plan?.organization?.settings?.currency || organization?.settings?.currency || 'INR';

  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const fullCost = round2(treatments.reduce((sum, t) => sum + (Number(t.cost) || 0), 0));
  const totalAfterDiscount = round2(plan?.totalCost ?? fullCost);
  const discountPercentFromPlan = Math.min(100, Math.max(0, Number(plan?.discountPercent) ?? 0));

  const fullCostBeforeLineDiscounts = round2(treatments.reduce((sum, t) => {
    const cost = Number(t.cost) || 0;
    const lineDiscount = Math.min(99.99, Math.max(0, Number(t.discount) || 0));
    if (lineDiscount > 0) return sum + round2(cost / (1 - lineDiscount / 100));
    return sum + cost;
  }, 0));
  const lineDiscountAmount = round2(Math.max(0, fullCostBeforeLineDiscounts - fullCost));
  const hasLineDiscount = lineDiscountAmount >= 0.01;

  const diffAmount = round2(Math.max(0, fullCost - totalAfterDiscount));
  let planDiscountAmount = 0;
  let planDiscountPercent = 0;
  if (discountPercentFromPlan > 0) {
    planDiscountPercent = discountPercentFromPlan;
    planDiscountAmount = round2(fullCost * (planDiscountPercent / 100));
  } else if (diffAmount >= 0.01) {
    planDiscountAmount = diffAmount;
    planDiscountPercent = fullCost > 0 ? Math.round((planDiscountAmount / fullCost) * 100) : 0;
  }
  const hasPlanDiscount = planDiscountAmount >= 0.01;
  const displayTotal = hasPlanDiscount ? round2(fullCost - planDiscountAmount) : fullCost;
  const hasAnyDiscount = hasLineDiscount || hasPlanDiscount;

  const statusLabels = {
    draft: 'Draft',
    active: 'Active',
    completed: 'Completed',
    cancelled: 'Cancelled'
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          {/* Header with Clinic Info */}
          <PDFHeader 
            organization={organization}
            clinic={clinicFromPatient}
            documentTitle="Treatment Plan"
            documentSubtitle="Comprehensive Treatment Plan Documentation"
          />

          {/* Details block top-right */}
          <View style={styles.detailsBlock}>
            <View style={styles.detailsRow}>
              <Text style={styles.label}>Plan Name</Text>
              <Text style={{ fontWeight: 600 }}>{plan?.name || '-'}</Text>
            </View>
            <View style={styles.detailsRow}>
              <Text style={styles.label}>Status</Text>
              <Text style={{ fontWeight: 600 }}>{statusLabels[plan?.status] || plan?.status || 'Draft'}</Text>
            </View>
            {plan?.createdAt && (
              <View style={styles.detailsRow}>
                <Text style={styles.label}>Created</Text>
                <Text style={{ fontWeight: 600 }}>{formatDate(plan.createdAt)}</Text>
              </View>
            )}
          </View>

          {/* Patient & Plan Info */}
          <View style={styles.headerRow}>
            {/* Patient Info */}
            <View style={styles.card}>
              <Text style={styles.cardTitle}>Patient Information</Text>
              {patient?.name && <Text style={styles.value}>{patient.name}</Text>}
              {patient?.patientId && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>
                  ID: {patient.patientId}
                </Text>
              )}
              {patient?.phone && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 4 }}>
                  Phone: {patient.phone}
                </Text>
              )}
              {patient?.email && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>
                  Email: {patient.email}
                </Text>
              )}
            </View>

            {/* Plan Summary */}
            <View style={styles.card}>
              <Text style={styles.cardTitle}>Plan Summary</Text>
              <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>
                Total Treatments: {treatments.length}
              </Text>
              {plan?.estimatedDuration && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>
                  Estimated Duration: {plan.estimatedDuration} minutes
                </Text>
              )}
              {plan?.totalCost !== undefined && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>
                  Total Cost: {formatPrice(plan.totalCost, currency)}
                </Text>
              )}
            </View>
          </View>

          {/* Description */}
          {plan?.description && (
            <>
              <View style={styles.divider} />
              <Text style={styles.sectionTitle}>Description</Text>
              <Text style={{ fontSize: 10, lineHeight: 1.5 }}>{plan.description}</Text>
            </>
          )}

          {/* Treatments Table */}
          {treatments.length > 0 && (
            <>
              <View style={styles.divider} />
              <Text style={styles.sectionTitle}>Treatments</Text>
              <View style={styles.table}>
                {/* Table Header */}
                <View style={[styles.tableRow, styles.tableHeader]}>
                  <Text style={styles.treatmentCol}>Treatment</Text>
                  <Text style={styles.categoryCol}>Category</Text>
                  <Text style={styles.teethCol}>Teeth</Text>
                  <Text style={styles.quantityCol}>Qty</Text>
                  <Text style={styles.durationCol}>Duration</Text>
                  <Text style={styles.costCol}>Cost</Text>
                </View>
                {/* Table Rows */}
                {treatments.map((treatmentEntry, index) => {
                  const treatment = treatmentEntry.treatment || treatmentEntry;
                  const treatmentName = treatment?.name || treatment?.treatmentName || 'Unknown Treatment';
                  const treatmentCategory = treatment?.category || 'N/A';
                  const teeth = Array.isArray(treatmentEntry.teeth) ? treatmentEntry.teeth : [];
                  const quantity = treatmentEntry.quantity || 1;
                  const duration = treatmentEntry.duration || treatment?.duration || 0;
                  const cost = treatmentEntry.cost || 0;
                  const lineDiscount = Math.min(100, Math.max(0, Number(treatmentEntry.discount) || 0));

                  return (
                    <View key={index} style={styles.tableRow}>
                      <Text style={styles.treatmentCol}>{treatmentName}</Text>
                      <Text style={styles.categoryCol}>{treatmentCategory}</Text>
                      <Text style={styles.teethCol}>
                        {teeth.length > 0 ? teeth.sort((a, b) => a - b).join(', ') : '-'}
                      </Text>
                      <Text style={styles.quantityCol}>{quantity}</Text>
                      <Text style={styles.durationCol}>{duration} min</Text>
                      <View style={styles.costCol}>
                        <Text>{formatPrice(cost, currency)}</Text>
                        {lineDiscount > 0 && (
                          <Text style={{ fontSize: 8, color: '#059669', marginTop: 1 }}>({lineDiscount}% off)</Text>
                        )}
                      </View>
                    </View>
                  );
                })}
              </View>

              {/* Totals: full cost before discounts, per-procedure discount, plan discount, total */}
              <View style={{ marginTop: 12, padding: 10, backgroundColor: '#F9FAFB', borderRadius: 6, border: '1px solid #E5E7EB' }}>
                {hasLineDiscount && (
                  <>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                      <Text style={{ fontSize: 10, color: '#374151' }}>Full cost (before discounts)</Text>
                      <Text style={{ fontSize: 10, fontWeight: 600 }}>{formatPrice(fullCostBeforeLineDiscounts, currency)}</Text>
                    </View>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                      <Text style={{ fontSize: 10, color: '#374151' }}>Discount (per procedure)</Text>
                      <Text style={{ fontSize: 10, fontWeight: 600, color: '#059669' }}>-{formatPrice(lineDiscountAmount, currency)}</Text>
                    </View>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                      <Text style={{ fontSize: 10, color: '#374151' }}>Subtotal</Text>
                      <Text style={{ fontSize: 10, fontWeight: 600 }}>{formatPrice(fullCost, currency)}</Text>
                    </View>
                  </>
                )}
                {!hasLineDiscount && (
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text style={{ fontSize: 10, color: '#374151' }}>Full cost (subtotal)</Text>
                    <Text style={{ fontSize: 10, fontWeight: 600 }}>{formatPrice(fullCost, currency)}</Text>
                  </View>
                )}
                {hasPlanDiscount && (
                  <>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                      <Text style={{ fontSize: 10, color: '#374151' }}>Plan discount ({planDiscountPercent}%)</Text>
                      <Text style={{ fontSize: 10, fontWeight: 600, color: '#059669' }}>-{formatPrice(planDiscountAmount, currency)}</Text>
                    </View>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 6, paddingTop: 6, borderTop: '1px solid #E5E7EB' }}>
                      <Text style={{ fontSize: 11, fontWeight: 700, color: '#1E40AF' }}>Total (after discount)</Text>
                      <Text style={{ fontSize: 11, fontWeight: 700, color: '#1E40AF' }}>{formatPrice(displayTotal, currency)}</Text>
                    </View>
                  </>
                )}
                {!hasPlanDiscount && (
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: hasLineDiscount ? 6 : 4, paddingTop: hasLineDiscount ? 6 : 0, borderTop: hasLineDiscount ? '1px solid #E5E7EB' : 'none' }}>
                    <Text style={{ fontSize: 11, fontWeight: 700, color: '#1E40AF' }}>Total</Text>
                    <Text style={{ fontSize: 11, fontWeight: 700, color: '#1E40AF' }}>{formatPrice(fullCost, currency)}</Text>
                  </View>
                )}
              </View>

              <View style={[styles.totalRow, { marginTop: 8 }]}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text style={styles.totalText}>Summary</Text>
                  <View style={{ flexDirection: 'row', gap: 24 }}>
                    {plan?.estimatedDuration && (
                      <Text style={styles.totalText}>
                        Duration: {plan.estimatedDuration} min
                      </Text>
                    )}
                    <Text style={styles.totalText}>
                      Total: {formatPrice(displayTotal, currency)}
                    </Text>
                  </View>
                </View>
              </View>
            </>
          )}

          {/* Footer */}
          <View style={styles.footer}>
            <Text style={styles.muted}>This treatment plan was generated electronically by DentalOS</Text>
            <Text style={[styles.muted, { marginTop: 4 }]}>
              {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })} at {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}
